import { useEffect } from 'react';
import { useSettings } from './context/SettingsContext';

/**
 * FaviconUpdater Component
 * Updates browser favicon and page title based on company settings
 * Handles both standard favicon (16x16) and Apple touch icon (180x180)
 */
const FaviconUpdater = () => {
  const { settings } = useSettings();

  useEffect(() => {
    if (settings?.logoUrl) {
      // ✅ Update standard favicon (16x16, 32x32)
      updateFavicon('icon', settings.logoUrl);
      updateFavicon('shortcut icon', settings.logoUrl);
      
      // ✅ Update Apple touch icon (better quality on iOS)
      updateAppleTouchIcon(settings.logoUrl);
      
      // ✅ Update manifest icon (for PWA)
      updateManifestIcon(settings.logoUrl);
    }

    // ✅ Update page title
    if (settings?.companyName) {
      document.title = `${settings.companyName} - Material Tracker`;
    }
  }, [settings]);

  return null;
};

// Helper function to update favicon
const updateFavicon = (rel, href) => {
  let link = document.querySelector(`link[rel*='${rel}']`);
  
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.appendChild(link);
  }
  
  // ✅ Add sizes attribute for better rendering
  link.sizes = '32x32';
  link.type = 'image/png';
  link.href = href;
};

// Helper function to update Apple touch icon
const updateAppleTouchIcon = (href) => {
  let link = document.querySelector("link[rel='apple-touch-icon']");
  
  if (!link) {
    link = document.createElement('link');
    link.rel = 'apple-touch-icon';
    document.head.appendChild(link);
  }
  
  // ✅ Apple recommends 180x180
  link.sizes = '180x180';
  link.href = href;
};

// Helper function to update manifest icon
const updateManifestIcon = (href) => {
  // ✅ Create/update manifest for PWA support
  const manifest = {
    name: 'Material Tracker',
    short_name: 'Tracker',
    icons: [
      {
        src: href,
        sizes: '192x192',
        type: 'image/png'
      },
      {
        src: href,
        sizes: '512x512',
        type: 'image/png'
      }
    ]
  };
  
  let manifestLink = document.querySelector("link[rel='manifest']");
  
  if (!manifestLink) {
    manifestLink = document.createElement('link');
    manifestLink.rel = 'manifest';
    document.head.appendChild(manifestLink);
  }
  
  // Convert manifest to data URL
  const manifestBlob = new Blob([JSON.stringify(manifest)], { type: 'application/json' });
  const manifestURL = URL.createObjectURL(manifestBlob);
  manifestLink.href = manifestURL;
};

export default FaviconUpdater;